/**
 * @name ObjectFilter
 * @ngdoc service
 * @memberOf modules
 * @description
 *
 *  Service to filter an object and ensure that required properties are set whilst stripping out unwanted properties
 *
 *  Filters should be an array of mixed elements. For simple nested properties, use a string (['ID', 'name'])
 *  For arrays, use an object syntax with an array of strings (['ID', 'name', {tiles: ['ID', 'name']}])
 *
 * @example
 *
 *  var object = {
 *    name: "Parent",
 *    children: [
 *      {
 *        name: 'Child',
 *        grandChildren: [
 *          {name: 'Grandchild'}
 *        ]
 *      }
 *    ]
 *  }
 *
 *  var filters = [
 *    'name',
 *    {
 *      children: [
 *        'name',
 *        grandChildren: ['name']
 *      ]
 *    }
 *  ]
 *
 *  var cleanedObject = new ObjectFilter(filters, object).getClean()
 *
 */

angular.module("modules").factory("ObjectFilter", function () {
  /**
   * @memberOf modules.ObjectFilter
   * @param {Array} filters an array of object properties
   * @param {Object} object
   */
  var ObjectFilter = function (filters, object) {
    /**
     * These are the filters that were passed in on initialization
     * @type {Object}
     */
    this._filters = filters;
    this._flattenedFilters = [];

    /**
     * This is the object that will be returned after being cleaned up
     * @type {Object}
     */
    this.cleanObject = {};

    /**
     * This is the original object that was passed in on initalization
     * @type {Object}
     */
    this.originalObject = object;

    if (!this._filters) throw new ReferenceError("ObjectFilter: no filters passed in!");

    if (!this.originalObject) throw new ReferenceError("ObjectFilter: no object passed in!");

    // Loop through all the filters
    this._filterProperties(this._prepareFilters(filters, this.originalObject));
  };

  ObjectFilter.prototype = {
    _filterProperties: function (filters) {
      for (var i = 0; i < filters.length; i++) {
        //   if (typeof filters[i] == 'object') {
        //     var filter = filters[i]
        //     for (var key in filter) {
        //       var array = this.getProperty(key, this.originalObject)
        //       for (var j = 0; j < array.length; j++) {
        //         this._filterProperties(filter[key], array[j])
        //       };
        //     };
        // } else {
        // First get the value of the filter
        var value = this.getProperty(filters[i], this.originalObject);

        // Then set the value on the cleanObject
        this.setProperty(filters[i], value);
        // }
      }
    },

    /**
     * Prepares an array of filters to be used by _filterProperties
     *   Flattens an array of mixed elements into a simple array of strings
     *
     * @param  {array} filters
     * @param  {object} object    the object to reference when getting values
     * @param  {string} [prefix]  an optional prefix to add to the filter keys (used with nested filter rules)
     * @return {object}
     */
    _prepareFilters: function (filters, object, prefix) {
      // Loop through all the filters
      for (var i = 0; i < filters.length; i++) {
        // If this is an object, it's a list of validation rules for each element of an array
        if (typeof filters[i] == "object") {
          // For easy access
          var filter = filters[i];

          // "Loop" through the object to get the first key
          for (var key in filters[i]) {
            // Get the array that this key is referencing
            var array = this.getProperty(key, object);

            if (!array) array = [];

            // For each element in the array
            for (var j = 0; j < array.length; j++) {
              // First set the cleaned object to an array so it doesn't get auto-set to an object
              this.setProperty(prefix ? prefix + "." + key : key, []);

              // For each set of child rules
              for (var x = 0; x < filter[key].length; x++) {
                // Grab the filter & key
                var childFilter = filter[key][x],
                  childKey = prefix ? prefix + "." + key : key;

                childKey += "." + j;

                // If the child filter is an object, it's another nested rule, so run over it again
                if (typeof childFilter == "object") this._prepareFilters([childFilter], array[j], childKey);
                // Otherwise push the key onto the flattened array
                else this._flattenedFilters.push(childKey + "." + childFilter);
              }
            }
          }

          // Nothing special to do! Hooray! Easy as pie: push the filter onto the array
        } else {
          var key = prefix ? prefix + "." + filters[i] : filters[i];
          this._flattenedFilters.push(key);
        }
      }

      // And, after all that work has been done,
      //  return the array of flattened filters
      return this._flattenedFilters;
    },

    /**
     * Returns the cleanObject
     * @return {Object} the cleaned object
     * @memberOf modules.ObjectFilter
     */
    getClean: function () {
      return this.cleanObject;
    },

    /**
     * Sets a property on the cleanObject by a dot notated key
     * @param {string} key    the dot-delimited string of the key in the object
     * @param {Object|string} the value to be set
     * @return {void}
     */
    setProperty: function (key, value) {
      // Set the cleanObject on a new var so that it's cleaner to access
      var object = this.cleanObject;

      // If the key is already on the object
      if (key in object) {
        // Set the value & return
        object[key] = value;
        return;
      }

      // Otherwise, split the key into an array
      var keyArray = key.split(".");

      // Loop through each segment of the keyArray
      for (var i = 0; i < keyArray.length; i++) {
        // When this reaches the end, set the value & return
        if (i == keyArray.length - 1) {
          object[keyArray[i]] = value;
          return;
        }

        // If this isn't at the end of the loop,
        // Check to see if the current key exists in the object
        // If it doesn't, create an empty object
        if (!(keyArray[i] in object)) {
          object[keyArray[i]] = {};
        }

        // Move on down the tree
        object = object[keyArray[i]];
      }
    },

    /**
     * [getProperty description]
     * @param  {string} key    dot-delimited string of the key in the object
     *                         i.e., user.firstName => object.user.firstName
     * @param  {Object} object the target object
     * @return {Object|string} the results of looking for the key in the object
     */
    getProperty: function (key, object) {
      // If the key is on the first level of the object, return it
      if (key in object) return object[key];

      // Otherwise, split the key into an array
      var keyArray = key.split(".");

      // Loop through each segment of the keyArray
      for (var i = 0; i < keyArray.length; i++) {
        // If the object isn't an object & the key isn't in the object,
        // That means that the key isn't in the object,
        // So return null
        if (typeof object != "object" || !(keyArray[i] in object)) return null;

        // At the end of each loop, descend down further into the object
        object = object[keyArray[i]];
      }

      // If the loop finishes
      // That means this is now the object that was being searched for
      // So return it
      return object;
    },
  };

  return ObjectFilter;
});
